import React from 'react';
import { Box, Chip, Autocomplete, TextField } from '@mui/material';
import { wikiTheme } from '../wikiTheme';

// Comprehensive DND 5e tag list
const DND_TAGS = [
  // Special Tags
  'Player Character',
  
  // Rarity
  'Common', 'Uncommon', 'Rare', 'Very Rare', 'Legendary', 'Artifact',
  
  // Item Types
  'Weapon', 'Armor', 'Shield', 'Potion', 'Scroll', 'Wand', 'Rod', 'Staff',
  'Ring', 'Amulet', 'Cloak', 'Boots', 'Gloves', 'Belt', 'Helmet', 'Bracers',
  'Magic Item', 'Consumable', 'Tool', 'Instrument', 'Focus', 'Component',
  
  // Weapon Types
  'Sword', 'Axe', 'Mace', 'Dagger', 'Spear', 'Bow', 'Crossbow', 'Polearm',
  'Hammer', 'Flail', 'Whip', 'Club', 'Quarterstaff', 'Sling',
  
  // Armor Types
  'Light Armor', 'Medium Armor', 'Heavy Armor', 'Plate', 'Chain Mail', 'Leather',
  
  // Damage Types
  'Fire', 'Cold', 'Lightning', 'Thunder', 'Acid', 'Poison', 'Necrotic',
  'Radiant', 'Psychic', 'Force', 'Slashing', 'Piercing', 'Bludgeoning',
  
  // Magic Schools
  'Abjuration', 'Conjuration', 'Divination', 'Enchantment', 'Evocation',
  'Illusion', 'Necromancy', 'Transmutation',
  
  // Creature Types
  'Aberration', 'Beast', 'Celestial', 'Construct', 'Dragon', 'Elemental',
  'Fey', 'Fiend', 'Giant', 'Humanoid', 'Monstrosity', 'Ooze', 'Plant',
  'Undead', 'Demon', 'Devil', 'Angel',
  
  // Alignments
  'Lawful Good', 'Neutral Good', 'Chaotic Good', 'Lawful Neutral', 'True Neutral',
  'Chaotic Neutral', 'Lawful Evil', 'Neutral Evil', 'Chaotic Evil',
  
  // Character Classes
  'Barbarian', 'Bard', 'Cleric', 'Druid', 'Fighter', 'Monk', 'Paladin',
  'Ranger', 'Rogue', 'Sorcerer', 'Warlock', 'Wizard', 'Artificer',
  
  // Character Races
  'Human', 'Elf', 'Dwarf', 'Halfling', 'Gnome', 'Half-Elf', 'Half-Orc',
  'Tiefling', 'Dragonborn', 'Orc', 'Goblin', 'Kobold', 'Drow',
  
  // Location Types
  'City', 'Town', 'Village', 'Dungeon', 'Cave', 'Forest', 'Mountain',
  'Ruins', 'Temple', 'Castle', 'Fortress', 'Tower', 'Tavern', 'Inn',
  'Shop', 'Guild Hall', 'Arena', 'Port', 'Desert', 'Swamp', 'Plane',
  'Continent', 'Island', 'Region', 'Province', 'Kingdom', 'Empire',
  'Lake', 'River', 'Ocean', 'Sea', 'Bay', 'Coast', 'Beach', 'Valley',
  'Plains', 'Tundra', 'Jungle', 'Hills', 'Cliff', 'Canyon', 'Volcano',
  'Road', 'Path', 'Bridge', 'Gate', 'Wall', 'Border', 'Outpost',
  
  // Factions & Organizations
  'Guild', 'Church', 'Military', 'Thieves Guild', 'Merchant', 'Cult',
  'Order', 'Brotherhood', 'Council', 'Empire', 'Kingdom',
  
  // Story Elements
  'Quest Item', 'Plot Hook', 'MacGuffin', 'Cursed', 'Blessed', 'Sentient',
  'Historical', 'Ancient', 'Mysterious', 'Prophetic', 'Secret',
  
  // Conditions & Effects
  'Healing', 'Buff', 'Debuff', 'Utility', 'Combat', 'Exploration', 'Social',
  'Stealth', 'Detection', 'Protection', 'Enhancement', 'Summoning',
  
  // NPC Roles
  'Merchant', 'Guard', 'Noble', 'Commoner', 'Priest', 'Mage', 'Warrior',
  'Craftsman', 'Scholar', 'Criminal', 'Leader', 'Servant', 'Ally', 'Enemy',
  'Neutral', 'Contact', 'Informant', 'Mentor', 'Rival',
  
  // Status
  'Active', 'Completed', 'Lost', 'Destroyed', 'Hidden', 'Known', 'Unknown',
  'Available', 'Unavailable', 'Locked', 'Accessible',
  
  // Misc
  'Important', 'Side Quest', 'Main Quest', 'Optional', 'Encounter',
  'Treasure', 'Loot', 'Reward', 'Trade Goods', 'Currency', 'Document',
  'Book', 'Map', 'Key', 'Vehicle', 'Mount', 'Pet', 'Familiar'
];

/**
 * Component for managing tags on wiki entries with DND 5e themed options
 */
const TagEditor = ({ tags = [], onChange, suggestions = [] }) => {
  // Merge DND_TAGS with suggestions and remove duplicates (case-insensitive)
  const getAllOptions = () => {
    const allTags = [...DND_TAGS, ...suggestions];
    const uniqueTags = new Map();
    
    // Use lowercase keys for deduplication, but preserve original casing
    allTags.forEach(tag => {
      const lowerTag = tag.toLowerCase();
      if (!uniqueTags.has(lowerTag)) {
        uniqueTags.set(lowerTag, tag);
      }
    });
    
    return Array.from(uniqueTags.values()).sort((a, b) => 
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
  };

  const allOptions = getAllOptions();

  return (
    <Box>
      <Autocomplete
        multiple
        value={tags}
        onChange={(event, newValue) => {
          onChange(newValue);
        }}
        options={allOptions}
        filterSelectedOptions
        renderInput={(params) => (
          <TextField
            {...params}
            label="Tags"
            placeholder="Search and select tags..."
            sx={{
              '& .MuiOutlinedInput-root': {
                color: wikiTheme.secondary,
                fontFamily: wikiTheme.font,
                '& fieldset': {
                  borderColor: wikiTheme.lightGray,
                },
                '&:hover fieldset': {
                  borderColor: wikiTheme.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: wikiTheme.primary,
                },
              },
              '& .MuiInputLabel-root': {
                color: wikiTheme.tertiary,
                fontFamily: wikiTheme.font,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: wikiTheme.primary,
              },
              '& .MuiAutocomplete-tag': {
                backgroundColor: wikiTheme.primary,
                color: wikiTheme.dark,
                fontFamily: wikiTheme.font,
                fontWeight: 'bold',
              },
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const isPlayerCharacter = option === 'Player Character';
            return (
              <Chip
                label={option}
                {...getTagProps({ index })}
                sx={{
                  backgroundColor: isPlayerCharacter ? '#4a90e2' : wikiTheme.primary,
                  color: isPlayerCharacter ? '#ffffff' : wikiTheme.dark,
                  fontFamily: wikiTheme.font,
                  fontWeight: isPlayerCharacter ? 'bold' : 'bold',
                  fontSize: isPlayerCharacter ? '0.9rem' : '0.8125rem',
                  border: isPlayerCharacter ? '2px solid #ffffff' : 'none',
                  boxShadow: isPlayerCharacter ? '0 0 8px rgba(74, 144, 226, 0.6)' : 'none',
                  '& .MuiChip-deleteIcon': {
                    color: isPlayerCharacter ? '#ffffff' : wikiTheme.dark,
                    '&:hover': {
                      color: isPlayerCharacter ? '#ffdddd' : '#8b5a1f',
                    },
                  },
                }}
              />
            );
          })
        }
        sx={{
          '& .MuiAutocomplete-popupIndicator': {
            color: wikiTheme.primary,
          },
          '& .MuiAutocomplete-clearIndicator': {
            color: wikiTheme.primary,
          },
        }}
        ListboxProps={{
          sx: {
            backgroundColor: wikiTheme.mediumGray,
            maxHeight: 400,
            '& .MuiAutocomplete-option': {
              fontFamily: wikiTheme.font,
              color: wikiTheme.secondary,
              '&:hover': {
                backgroundColor: wikiTheme.lightGray,
              },
              '&[aria-selected="true"]': {
                backgroundColor: wikiTheme.dark,
                '&:hover': {
                  backgroundColor: wikiTheme.lightGray,
                },
              },
            },
          },
        }}
      />
    </Box>
  );
};

export default TagEditor;
